
import { toast } from "@/hooks/use-toast";

interface HandleFareHarborBookingParams {
  e: React.MouseEvent<HTMLElement>;
  itemId: number;
  flowId?: number;
  lightframe?: boolean;
}

/**
 * Handles FareHarbor booking links with fallbacks
 */
export const handleFareHarborBooking = ({
  e,
  itemId,
  flowId = 1219724,
  lightframe = true
}: HandleFareHarborBookingParams): void => {
  e.preventDefault();
  
  try {
    // Track the event for analytics
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'begin_booking', {
        event_category: 'engagement',
        item_id: itemId
      });
    }

    // Open direct URL in new window/tab
    const url = `https://fareharbor.com/embeds/book/catsabouttown/items/${itemId}/calendar/?flow=${flowId}&full-items=yes`;
    window.open(url, '_blank');
  } catch (error) {
    console.error('Failed to open booking calendar:', error);
    toast({
      title: "Booking Error",
      description: "We're having trouble opening the booking calendar. Please try again later.",
      variant: "destructive"
    });
    
    // Ultimate fallback - navigate to general booking page
    window.location.href = "/booking";
  }
};

/**
 * Loads the FareHarbor API script if not already loaded
 * @returns Promise that resolves when script is loaded
 */
export const loadFareHarborScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Skip if not in browser environment
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    
    if (window.FH) {
      resolve();
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://fareharbor.com/embeds/api/v1/?autolightframe=yes';
    script.async = true;
    
    script.onload = () => {
      console.log('FareHarbor script loaded successfully');
      resolve();
    };
    script.onerror = () => {
      console.error('Failed to load FareHarbor script');
      reject(new Error('Failed to load FareHarbor script'));
    };
    
    document.body.appendChild(script);
  });
};

// Extend window with FareHarbor API types
declare global {
  interface Window {
    FH?: {
      open: (options: {
        items: { id: number }[];
        lightframe?: boolean;
        fullItems?: string;
        flow?: number;
        fallback?: string;
      }) => void;
    };
    gtag?: (command: string, action: string, params?: any) => void;
  }
}
